import PropTypes from 'prop-types';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styles from './OnboardingBilling.module.less';
import AnalyticsCore from '../../../Utils/AnalyticsHandler/AnalyticsCore';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import { ONBOARDING_BILLING_FLOW_STEPS_WITH_CREDIT_CARD, BASE_URL } from './constants';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler';
import CreditCardIntegratedForm from './CreditCardStripe/CreditCardForm';

import PiplSearchLogo from '../../../Components/PiplSearchLogo';
import OrganizationDetailsForm from './OrganizationDetails/OnboardingUpdateOrganizationDetailsForm';
import AppConfig from '../../../Config/AppConfig';

class OnboardingBilling extends React.Component {
  steps = ['Billing', 'Payment', 'Confirmation'];

  manual_steps = ['Billing', 'Confirmation'];

  state = {
    cc: {},
    orgFormIsValid: false,
    ccFormIsValid: false,
  };

  // eslint-disable-next-line react/no-deprecated
  componentWillMount() {}

  componentDidMount() {
    const eventData = AnalyticsCore.buildEvent({
      user: this.props.user,
      tracking: this.props.tracking,
      eventName: AnalyticsEventNames.ONBOARDING_BILLING_FLOW_INIT,
      eventData: {
        referrer: document.referrer || '',
        initialStep: this.getCurrentStepNumber(),
      },
      // check how to get referrer from signup form
    });
    AnalyticsCore.sendEvent(eventData);

    if (!this.props.organization && !this.props.isFetchingOrganization) {
      this.props.fetchOrganization();
    }

    if (!this.props.isFetchingPaymentMethod) {
      this.props.fetchPaymentMethod();
    }
  }

  componentWillUnmount() {}

  hasOrganizationAddress() {
    const { organization } = this.props;
    return (
      organization && organization.address && organization.postal_code && organization.city && organization.country_code
    );
  }

  isPaymentMethodExists() {
    return this.props.paymentMethod && this.props.paymentMethod.is_chargeable;
  }

  isLoadingData() {
    return (
      this.props.isFetchingPaymentMethod || this.props.isFetchingOrganization || this.props.isFetchingSubscriptions
    );
  }

  getCurrentStepNumber = () => {
    if (!this.hasOrganizationAddress()) {
      return ONBOARDING_BILLING_FLOW_STEPS_WITH_CREDIT_CARD.BILLING;
    }

    if (!this.isPaymentMethodExists() && this.props.signupPaymentMethod !== 'MANUAL') {
      return ONBOARDING_BILLING_FLOW_STEPS_WITH_CREDIT_CARD.PAYMENT;
    }

    return ONBOARDING_BILLING_FLOW_STEPS_WITH_CREDIT_CARD.CONFIRMATION;
  };

  renderStepsNavbar = () => {
    const steps = this.props.signupPaymentMethod === 'MANUAL' ? this.manual_steps : this.steps;
    return steps.map((step, i) => {
      const currentStep = this.getCurrentStepNumber() - 1;
      const stepNum = i + 1;

      const classes = [styles.step];
      if (i < currentStep) {
        classes.push(styles.previousStep);
      } else if (i === currentStep) {
        classes.push(styles.currentStep);
      }

      return (
        <span key={`step-${step}`} className={classes.join(' ')}>
          <i className={styles.stepNumber}>{stepNum}</i>
          <span className={styles.stepName}>{gettext(step)}</span>
        </span>
      );
    });
  };

  renderOrganizationStep = () => {
    const submitDisabled = this.props.isUpdatingOrganization || !this.state.orgFormIsValid;

    const eventData = AnalyticsCore.buildEvent({
      user: this.props.user,
      tracking: this.props.tracking,
      eventName: AnalyticsEventNames.ONBOARDING_BILLING_FLOW_BILLING_DETAILS_SUBMIT,
      eventData: { referrer: document.referrer || '' },
    });

    return (
      <div className={styles.formWrapper}>
        <div className={styles.title}>{gettext('Organization Details')}</div>
        <OrganizationDetailsForm
          serverErrorByField={this.props.serverErrorByField}
          user={this.props.user}
          ref={(input) => (this._setOrganizationBillingDetails = input)} //eslint-disable-line
          updatedState={(orgFormIsValid) => {
            if (orgFormIsValid !== this.state.orgFormIsValid) {
              this.setState({ orgFormIsValid });
            }
          }}
          onSubmit={
            (name, invoiceEmail, website, phone, address, city, country, state, postalCode) =>
              this.props.updateOrganizationDetails({
                name,
                invoiceEmail,
                website,
                phone,
                address,
                city,
                country_code: country,
                state,
                postal_code: postalCode,
                eventData,
              })
            // eslint-disable-next-line
          }
        />
        <AnalyticsHandler eventName={AnalyticsEventNames.ONBOARDING_BILLING_FLOW_BILLING_DETAILS_SUBMIT} eventData={{}}>
          <button
            type="button"
            tabIndex={0}
            className={[styles.submitButton, submitDisabled ? styles.disabled : null].join(' ')}
            onClick={() => this._setOrganizationBillingDetails.submit()}
            disabled={submitDisabled}
          >
            {!this.props.isUpdatingOrganization ? gettext('Proceed') : gettext('Saving Organization Details...')}
          </button>
        </AnalyticsHandler>
        <div className={styles.agreeToTerms}>
          {gettext('By clicking "Proceed", you agree to Pipl\'s')}
          <br />
          <a href={`${AppConfig.TERMS_AND_CONDITIONS_URL_VERIFY_API}`} target="_blank">
            {gettext('Conditions of Use')}
          </a>{' '}
          {gettext('and')}{' '}
          <a href={`${BASE_URL}/privacy/`} target="_blank">
            {gettext('Privacy Notice')}
          </a>
        </div>
      </div>
    );
  };

  onCreditCardUpdate(update) {
    const ccState = this.state.cc;
    this.setState({
      cc: {
        ...ccState,
        ...update,
      },
    });
  }

  renderCreditCardStep = () => {
    const eventData = AnalyticsCore.buildEvent({
      user: this.props.user,
      tracking: this.props.tracking,
      eventName: AnalyticsEventNames.ONBOARDING_BILLING_FLOW_CREDIT_CARD_DETAILS_STEP,
      eventLabel: 'Payment Submitted',
      eventData: {
        referrer: document.referrer || '',
        paymentMethod: this.props?.paymentMethod?.method,
        isChargeable: this.props?.paymentMethod?.is_chargeable,
      },
    });

    let error = this.props.status;
    if (this.props.stripeTokenStatus) {
      if (typeof this.props.stripeTokenStatus === 'string') {
        error = error || this.props.stripeTokenStatus;
      } else {
        error = error || this.props.stripeTokenStatus.message;
      }

      const errorEvent = { ...eventData };
      errorEvent.eventName = `${eventData.eventName}_FAILED`;
      errorEvent.eventLabel = error;
      AnalyticsCore.sendEvent(errorEvent);
    }

    const buttonDisabled = !this.state.ccFormIsValid || this.props.isFetching;

    return (
      <div className={styles.formWrapper}>
        <div className={styles.title}>{gettext('Payment Method')}</div>

        <CreditCardIntegratedForm
          ref={(input) => (this._createCreditCard = input)} //eslint-disable-line
          form={this.state.cc}
          createStripeTokenInProgress={this.props.createStripeTokenInProgress}
          createStripeTokenFailed={this.props.createStripeTokenFailed}
          onCreditCardUpdate={(update) => this.onCreditCardUpdate(update)}
          organization={this.props.organization}
          onSubmit={(token, card) => this.props.createCreditCard(token, card, eventData)}
          updatedState={(ccFormIsValid) => {
            if (ccFormIsValid !== this.state.ccFormIsValid) {
              this.setState({ ccFormIsValid });
            }
          }}
        />

        <AnalyticsHandler
          eventName={AnalyticsEventNames.ONBOARDING_BILLING_FLOW_PAYMENT_SUBMIT}
          eventLabel="Credit Card Submitted"
          eventData={{
            paymentMethod: this.props?.paymentMethod?.method,
            isChargeable: this.props?.paymentMethod?.is_chargeable, //eslint-disable-line
          }}
        >
          <button
            type="button"
            tabIndex={0}
            test-id="add-credit-card-button"
            className={[styles.submitButton, buttonDisabled ? styles.disabled : null].join(' ')}
            disabled={buttonDisabled}
            onClick={() => this._createCreditCard.submit()}
          >
            {gettext('Submit')}
          </button>
        </AnalyticsHandler>
        {error && (
          <div className={[styles.statusMessage, this.props.stripeTokenStatus ? styles.error : null].join(' ')}>
            {error}
          </div>
        )}
      </div>
    );
  };

  renderConfirmationStep = () => (
    <div>
      <div className={styles.title}>{gettext('Billing Information Confirmation')}</div>
      <div className={styles.confirmationText}>{gettext('You can start using Trust!')}</div>
      <div>
        <AnalyticsHandler eventName={AnalyticsEventNames.ONBOARDING_BILLING_FLOW_START_USING_TRUST_API}>
          <a className={styles.buttonLink} href={`${BASE_URL}/api/`}>
            {gettext('Start using Pipl Trust')}
          </a>
        </AnalyticsHandler>
      </div>
    </div>
  );

  renderCurrentStep = () => {
    if (this.isLoadingData()) {
      return (
        <div>
          <Skeleton height={31} />
          <div style={{ height: 16 }} />
          <Skeleton height={30} count={11} />
          <div style={{ height: 15 }} />
          <Skeleton height={50} />
        </div>
      );
    }
    const currentStepNumber = this.getCurrentStepNumber();
    switch (currentStepNumber) {
      case ONBOARDING_BILLING_FLOW_STEPS_WITH_CREDIT_CARD.BILLING:
        return this.renderOrganizationStep();
      case ONBOARDING_BILLING_FLOW_STEPS_WITH_CREDIT_CARD.PAYMENT:
        return this.renderCreditCardStep();
      case ONBOARDING_BILLING_FLOW_STEPS_WITH_CREDIT_CARD.CONFIRMATION:
        return this.renderConfirmationStep();
      default:
        return null;
    }
  };

  render() {
    return (
      <div className={styles.main}>
        <div className={styles.logo}>
          <PiplSearchLogo />
        </div>
        <div className={styles.navbar}>{this.renderStepsNavbar()}</div>
        <div className={styles.flowWrapper}>
          <div className={styles.stepData}>{this.renderCurrentStep()}</div>
        </div>
        {/* <NotificationContainer /> */}
      </div>
    );
  }
}

OnboardingBilling.propTypes = {
  user: PropTypes.object,
  tracking: PropTypes.object,
  organization: PropTypes.object,
  paymentMethod: PropTypes.object,
  signupPaymentMethod: PropTypes.string,

  fetchOrganization: PropTypes.func,
  fetchPaymentMethod: PropTypes.func,
  updateOrganizationDetails: PropTypes.func,

  createStripeTokenInProgress: PropTypes.func,
  createStripeTokenFailed: PropTypes.func,

  isFetchingSubscriptions: PropTypes.bool,
  isFetchingOrganization: PropTypes.bool,
  isFetchingPaymentMethod: PropTypes.bool,

  status: PropTypes.any,
  isFetching: PropTypes.bool,

  isUpdatingOrganization: PropTypes.bool,
  stripeTokenStatus: PropTypes.string,
  createCreditCard: PropTypes.func,

  serverErrorByField: PropTypes.object,
};
export default OnboardingBilling;
